"use client";

import { useEffect, useState } from "react";

const CONFERENCES_URL = "https://eventscons.com/eventbackendnew/api/conferences/all";
const BANNER_ADD_URL = "https://eventscons.com/eventbackendnew/api/conference-banner";
const BANNER_GET_URL = "https://eventscons.com/eventbackendnew/api/conference-banner-get";
const BANNER_UPDATE_URL = "https://eventscons.com/eventbackendnew/api/conference-banner-update";
const DOORTIME_POST_URL = "https://eventscons.com/eventbackendnew/api/doortime";

const CPHOTOS_ADD_URL = "https://eventscons.com/eventbackendnew/api/cphotos";
const CPHOTOS_GET_URL = "https://eventscons.com/eventbackendnew/api/cphotos";
const CPHOTOS_EDIT_URL = "https://eventscons.com/eventbackendnew/api/cphotos/edit";
const CPHOTOS_DELETE_URL = "https://eventscons.com/eventbackendnew/api/cphotos/delete";

const CONFERENCE_VIDEO_POST_URL =
  "https://eventscons.com/eventbackendnew/api/conference-upload-video";
const CONFERENCE_VIDEO_GET_URL =
  "https://eventscons.com/eventbackendnew/api/conference-get-video-link";

function getAuthToken() {
  if (typeof window === "undefined") return null;
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("access_token")
  );
}

export default function ConferenceBanner() {
  const [conferences, setConferences] = useState([]);
  const [selectedConference, setSelectedConference] = useState("");
  const [banner, setBanner] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [doorTime, setDoorTime] = useState("");
  const [loadingConferences, setLoadingConferences] = useState(false);
  const [loadingBanner, setLoadingBanner] = useState(false);
  const [loadingDoorTime, setLoadingDoorTime] = useState(false);
  const [savingDoorTime, setSavingDoorTime] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);

  const [conferencePhotos, setConferencePhotos] = useState([]);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [photoSubmitting, setPhotoSubmitting] = useState(false);
  const [editingPhotoId, setEditingPhotoId] = useState(null);
  const [editPhotoFile, setEditPhotoFile] = useState(null);

  const [videoLinks, setVideoLinks] = useState([""]);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoSubmitting, setVideoSubmitting] = useState(false);

  const formatTo12Hour = (time24) => {
    if (!time24) return "";

    const [hourStr, minute] = time24.split(":");
    let hour = parseInt(hourStr, 10);
    const ampm = hour >= 12 ? "PM" : "AM";

    hour = hour % 12;
    hour = hour === 0 ? 12 : hour;

    return `${hour}:${minute} ${ampm}`;
  };

  const formatTo24Hour = (timeValue) => {
    if (!timeValue) return "";

    if (/^\d{2}:\d{2}:\d{2}$/.test(timeValue)) {
      return timeValue.slice(0, 5);
    }

    if (/^\d{2}:\d{2}$/.test(timeValue)) {
      return timeValue;
    }

    const [time, modifier] = timeValue.split(" ");
    if (!time || !modifier) return "";

    let [hours, minutes] = time.split(":");
    hours = parseInt(hours, 10);

    if (modifier.toUpperCase() === "PM" && hours !== 12) {
      hours += 12;
    }

    if (modifier.toUpperCase() === "AM" && hours === 12) {
      hours = 0;
    }

    return `${String(hours).padStart(2, "0")}:${minutes}`;
  };

  const fetchConferences = async () => {
    try {
      setLoadingConferences(true);
      setError("");

      const token = getAuthToken();

      const res = await fetch(CONFERENCES_URL, {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!res.ok) {
        throw new Error("Failed to load conferences");
      }

      const json = await res.json();
      const list = json.data || [];
      setConferences(list);

      if (list.length > 0 && !selectedConference) {
        setSelectedConference(String(list[0].id));
      }
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoadingConferences(false);
    }
  };

  const fetchDoorTime = async (conferenceId) => {
    const confId = conferenceId || selectedConference;

    if (!confId) {
      setDoorTime("");
      return;
    }

    try {
      setLoadingDoorTime(true);

      const token = getAuthToken();

      const res = await fetch(
        `https://eventscons.com/eventbackendnew/api/conference-doortime/${confId}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      const json = await res.json();

      if (!res.ok) {
        setDoorTime("");
        return;
      }

      const savedTime =
        json?.data?.doortime ||
        json?.data?.door_time ||
        json?.doortime ||
        json?.door_time ||
        "";

      setDoorTime(formatTo24Hour(savedTime));
    } catch (err) {
      setDoorTime("");
    } finally {
      setLoadingDoorTime(false);
    }
  };

  const saveDoorTime = async (conferenceId) => {
    const confId = conferenceId || selectedConference;

    if (!confId || !doorTime) return;

    const token = getAuthToken();
    const formattedDoorTime = formatTo12Hour(doorTime);

    const res = await fetch(`${DOORTIME_POST_URL}/${confId}`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        doortime: formattedDoorTime,
      }),
    });

    const json = await res.json();

    if (!res.ok) {
      throw new Error(json.message || "Failed to save door time");
    }

    return json;
  };

  const handleSaveDoorTime = async () => {
    if (!selectedConference) {
      alert("Please select a conference first");
      return;
    }

    if (!doorTime) {
      alert("Please choose door time");
      return;
    }

    try {
      setSavingDoorTime(true);
      setError("");
      setSuccess("");

      await saveDoorTime(selectedConference);
      setSuccess("Door time saved successfully");
      await fetchDoorTime(selectedConference);
    } catch (err) {
      setError(err.message || "Failed to save door time");
    } finally {
      setSavingDoorTime(false);
    }
  };

  const fetchBanner = async (conferenceId) => {
    const confId = conferenceId || selectedConference;

    if (!confId) {
      setBanner(null);
      return;
    }

    try {
      setLoadingBanner(true);
      setError("");
      setSuccess("");

      const token = getAuthToken();

      const res = await fetch(`${BANNER_GET_URL}/${confId}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const json = await res.json();

      if (!res.ok) {
        setBanner(null);
        throw new Error(json.message || "Banner not found");
      }

      setBanner(json.data || null);
    } catch (err) {
      setBanner(null);
      setError(err.message || "Something went wrong");
    } finally {
      setLoadingBanner(false);
    }
  };

  const handleAddBanner = async (e) => {
    e.preventDefault();

    if (!selectedConference) {
      alert("Please select a conference first");
      return;
    }

    if (!bannerFile) {
      alert("Please select a banner image");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      setSuccess("");

      const token = getAuthToken();
      if (!token) throw new Error("Authentication token not found");

      const formData = new FormData();
      formData.append("banner_image", bannerFile);

      const res = await fetch(`${BANNER_ADD_URL}/${selectedConference}`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.message || "Failed to add banner");
      }

      setSuccess("Banner added successfully");
      setBannerFile(null);
      await fetchBanner(selectedConference);
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditBanner = async (e) => {
    e.preventDefault();

    if (!selectedConference) {
      alert("Please select a conference first");
      return;
    }

    if (!bannerFile) {
      alert("Please select a new banner image");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      setSuccess("");

      const token = getAuthToken();
      if (!token) throw new Error("Authentication token not found");

      const formData = new FormData();
      formData.append("banner_image", bannerFile);
      formData.append("_method", "PUT");

      const res = await fetch(`${BANNER_UPDATE_URL}/${selectedConference}`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.message || "Failed to update banner");
      }

      setSuccess("Banner updated successfully");
      setBannerFile(null);
      setIsEditMode(false);
      await fetchBanner(selectedConference);
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const fetchConferencePhotos = async (conferenceId) => {
    const confId = conferenceId || selectedConference;

    if (!confId) {
      setConferencePhotos([]);
      return;
    }

    try {
      setPhotoLoading(true);

      const token = getAuthToken();

      const res = await fetch(`${CPHOTOS_GET_URL}/${confId}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const json = await res.json();

      if (!res.ok) {
        setConferencePhotos([]);
        return;
      }

      setConferencePhotos(Array.isArray(json.data) ? json.data : []);
    } catch (err) {
      setConferencePhotos([]);
    } finally {
      setPhotoLoading(false);
    }
  };

  const handleAddPhotos = async (e) => {
    e.preventDefault();

    if (!selectedConference) {
      alert("Please select a conference first");
      return;
    }

    if (!photoFiles.length) {
      alert("Please select one or more images");
      return;
    }

    try {
      setPhotoSubmitting(true);
      setError("");
      setSuccess("");

      const token = getAuthToken();
      if (!token) throw new Error("Authentication token not found");

      const formData = new FormData();

      photoFiles.forEach((file) => {
        formData.append("photos[]", file);
      });

      const res = await fetch(`${CPHOTOS_ADD_URL}/${selectedConference}`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(
          typeof json.message === "string"
            ? json.message
            : "Failed to upload photos"
        );
      }

      setSuccess("Conference photos uploaded successfully");
      setPhotoFiles([]);
      await fetchConferencePhotos(selectedConference);
    } catch (err) {
      setError(err.message || "Failed to upload photos");
    } finally {
      setPhotoSubmitting(false);
    }
  };

  const handleEditPhoto = async (photoId) => {
    if (!editPhotoFile) {
      alert("Please select a new image");
      return;
    }

    try {
      setPhotoSubmitting(true);
      setError("");
      setSuccess("");

      const token = getAuthToken();
      if (!token) throw new Error("Authentication token not found");

      const formData = new FormData();
      formData.append("photo", editPhotoFile);

      const res = await fetch(`${CPHOTOS_EDIT_URL}/${photoId}`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(
          typeof json.message === "string"
            ? json.message
            : "Failed to update photo"
        );
      }

      setSuccess("Photo updated successfully");
      setEditingPhotoId(null);
      setEditPhotoFile(null);
      await fetchConferencePhotos(selectedConference);
    } catch (err) {
      setError(err.message || "Failed to update photo");
    } finally {
      setPhotoSubmitting(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this photo?");
    if (!confirmDelete) return;

    try {
      setPhotoSubmitting(true);
      setError("");
      setSuccess("");

      const token = getAuthToken();
      if (!token) throw new Error("Authentication token not found");

      const res = await fetch(`${CPHOTOS_DELETE_URL}/${photoId}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.message || "Failed to delete photo");
      }

      setSuccess("Photo deleted successfully");
      await fetchConferencePhotos(selectedConference);
    } catch (err) {
      setError(err.message || "Failed to delete photo");
    } finally {
      setPhotoSubmitting(false);
    }
  };

  const fetchConferenceVideos = async (conferenceId) => {
    const confId = conferenceId || selectedConference;

    if (!confId) {
      setVideoLinks([""]);
      return;
    }

    try {
      setVideoLoading(true);

      const token = getAuthToken();

      const res = await fetch(`${CONFERENCE_VIDEO_GET_URL}/${confId}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const json = await res.json();

      if (!res.ok) {
        setVideoLinks([""]);
        return;
      }

      const rawData = json?.data;

      let links = [];

      if (Array.isArray(rawData)) {
        links = rawData;
      } else if (Array.isArray(rawData?.conference_video_link)) {
        links = rawData.conference_video_link;
      } else if (Array.isArray(json?.conference_video_link)) {
        links = json.conference_video_link;
      } else if (typeof rawData?.conference_video_link === "string") {
        links = [rawData.conference_video_link];
      } else if (typeof json?.conference_video_link === "string") {
        links = [json.conference_video_link];
      } else if (Array.isArray(rawData?.links)) {
        links = rawData.links;
      }

      setVideoLinks(
        links.length ? links.map((item) => String(item || "")) : [""]
      );
    } catch (err) {
      setVideoLinks([""]);
    } finally {
      setVideoLoading(false);
    }
  };

  const handleSaveConferenceVideos = async (e) => {
    e.preventDefault();

    if (!selectedConference) {
      alert("Please select a conference first");
      return;
    }

    const cleanedLinks = videoLinks
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    if (!cleanedLinks.length) {
      alert("Please add at least one video link");
      return;
    }

    try {
      setVideoSubmitting(true);
      setError("");
      setSuccess("");

      const token = getAuthToken();
      if (!token) throw new Error("Authentication token not found");

      const formData = new FormData();
      formData.append("conference_id", selectedConference);

      cleanedLinks.forEach((link) => {
        formData.append("conference_video_link[]", link);
      });

      const res = await fetch(CONFERENCE_VIDEO_POST_URL, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(
          typeof json.message === "string"
            ? json.message
            : "Failed to save conference videos"
        );
      }

      setSuccess("Conference videos saved successfully");
      await fetchConferenceVideos(selectedConference);
    } catch (err) {
      setError(err.message || "Failed to save conference videos");
    } finally {
      setVideoSubmitting(false);
    }
  };

  const handleVideoChange = (index, value) => {
    setVideoLinks((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const handleAddVideoField = () => {
    setVideoLinks((prev) => [...prev, ""]);
  };

  const handleRemoveVideoField = (index) => {
    setVideoLinks((prev) => {
      if (prev.length === 1) return [""];
      return prev.filter((_, i) => i !== index);
    });
  };

  useEffect(() => {
    fetchConferences();
  }, []);

  useEffect(() => {
    if (selectedConference) {
      fetchBanner(selectedConference);
      fetchDoorTime(selectedConference);
      fetchConferencePhotos(selectedConference);
      fetchConferenceVideos(selectedConference);
    }
  }, [selectedConference]);

  return (
    <div
      style={{
        padding: "24px",
        maxWidth: "1100px",
        width: "100%",
        margin: "0 auto",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      }}
    >
      <h1 style={{ fontSize: "30px", fontWeight: 700, marginBottom: 16 }}>
        Conference Banner
      </h1>

      {error && (
        <div
          style={{
            marginBottom: 16,
            padding: 12,
            borderRadius: 8,
            backgroundColor: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#b91c1c",
          }}
        >
          {error}
        </div>
      )}

      {success && (
        <div
          style={{
            marginBottom: 16,
            padding: 12,
            borderRadius: 8,
            backgroundColor: "#f0fdf4",
            border: "1px solid #bbf7d0",
            color: "#166534",
          }}
        >
          {success}
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginBottom: 20,
          flexWrap: "wrap",
        }}
      >
        <select
          value={selectedConference}
          onChange={(e) => {
            setSelectedConference(e.target.value);
            setBannerFile(null);
            setDoorTime("");
            setIsEditMode(false);
            setPhotoFiles([]);
            setEditPhotoFile(null);
            setEditingPhotoId(null);
            setVideoLinks([""]);
            setError("");
            setSuccess("");
          }}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #cbd5e1",
            minWidth: 240,
            fontSize: 14,
          }}
        >
          <option value="">
            {loadingConferences ? "Loading conferences..." : "Select Conference"}
          </option>
          {conferences.map((conf) => (
            <option key={conf.id} value={conf.id}>
              {conf.title || conf.name || `Conference ${conf.id}`}
            </option>
          ))}
        </select>

        <button
          onClick={() => {
            fetchBanner(selectedConference);
            fetchDoorTime(selectedConference);
            fetchConferencePhotos(selectedConference);
            fetchConferenceVideos(selectedConference);
          }}
          disabled={!selectedConference}
          style={{
            padding: "8px 16px",
            borderRadius: 8,
            border: "none",
            backgroundColor: "#9B1321",
            color: "white",
            fontWeight: 500,
            cursor: selectedConference ? "pointer" : "not-allowed",
          }}
        >
          Get Banner
        </button>

        <button
          onClick={() => {
            setIsEditMode(false);
            setError("");
            setSuccess("");
          }}
          disabled={!selectedConference}
          style={{
            padding: "8px 16px",
            borderRadius: 8,
            border: "none",
            backgroundColor: "#16a34a",
            color: "white",
            fontWeight: 500,
            cursor: selectedConference ? "pointer" : "not-allowed",
          }}
        >
          Add Banner
        </button>

        <button
          onClick={() => {
            setIsEditMode(true);
            setError("");
            setSuccess("");
          }}
          disabled={!selectedConference || !banner}
          style={{
            padding: "8px 16px",
            borderRadius: 8,
            border: "none",
            backgroundColor: "#2563eb",
            color: "white",
            fontWeight: 500,
            cursor: selectedConference && banner ? "pointer" : "not-allowed",
          }}
        >
          Edit Banner
        </button>
      </div>

      {loadingBanner ? (
        <p>Loading banner...</p>
      ) : banner ? (
        <div
          style={{
            marginBottom: 24,
            padding: 16,
            borderRadius: 12,
            backgroundColor: "#fff",
            border: "1px solid #e5e7eb",
            boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
          }}
        >
          <h3 style={{ marginBottom: 12 }}>Current Banner</h3>

          <p style={{ marginBottom: 10, color: "#374151", fontSize: 14 }}>
            <strong>Door Time:</strong>{" "}
            {loadingDoorTime
              ? "Loading..."
              : doorTime
              ? formatTo12Hour(doorTime)
              : "Not set"}
          </p>

          {(banner.banner_url || banner.image_url) && (
            <img
              src={banner.banner_url || banner.image_url}
              alt="Conference Banner"
              style={{
                marginTop: 12,
                maxWidth: "100%",
                width: "420px",
                borderRadius: 10,
                border: "1px solid #e5e7eb",
                objectFit: "cover",
              }}
            />
          )}
        </div>
      ) : (
        <p>
          {selectedConference
            ? "No banner found for this conference."
            : "Please select a conference."}
        </p>
      )}

      <div
        style={{
          marginTop: 24,
          padding: 16,
          borderRadius: 12,
          backgroundColor: "#f9fafb",
          border: "1px solid #e5e7eb",
        }}
      >
        <h3 style={{ marginBottom: 12, fontSize: 18, fontWeight: 600 }}>
          {isEditMode ? "Edit Banner" : "Add Banner"}
        </h3>

        <form onSubmit={isEditMode ? handleEditBanner : handleAddBanner}>
          <div style={{ marginBottom: 12 }}>
            <label
              style={{
                display: "block",
                marginBottom: 6,
                fontSize: 14,
              }}
            >
              Door Time
            </label>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <input
                type="time"
                value={doorTime}
                onChange={(e) => setDoorTime(e.target.value)}
                style={{
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid #cbd5e1",
                  width: "220px",
                  fontSize: 14,
                  backgroundColor: "#fff",
                }}
              />

              <button
                type="button"
                onClick={handleSaveDoorTime}
                disabled={!selectedConference || !doorTime || savingDoorTime}
                style={{
                  padding: "10px 16px",
                  borderRadius: 8,
                  border: "none",
                  backgroundColor: "#9B1321",
                  color: "white",
                  fontWeight: 500,
                  cursor:
                    !selectedConference || !doorTime || savingDoorTime
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                {savingDoorTime ? "Saving..." : "Save Time"}
              </button>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label
              style={{
                display: "block",
                marginBottom: 6,
                fontSize: 14,
              }}
            >
              Banner Image
            </label>

            <input
              type="file"
              accept="image/*"
              onChange={(e) =>
                setBannerFile(
                  e.target.files && e.target.files[0] ? e.target.files[0] : null
                )
              }
            />
          </div>

          {bannerFile && (
            <div style={{ marginBottom: 12 }}>
              <p style={{ fontSize: 13, color: "#374151" }}>
                Selected file: {bannerFile.name}
              </p>
            </div>
          )}

          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="submit"
              disabled={!selectedConference || submitting}
              style={{
                padding: "8px 16px",
                borderRadius: 8,
                border: "none",
                backgroundColor: isEditMode ? "#2563eb" : "#16a34a",
                color: "white",
                fontWeight: 500,
                cursor: !selectedConference || submitting ? "not-allowed" : "pointer",
              }}
            >
              {submitting
                ? "Please wait..."
                : isEditMode
                ? "Update Banner"
                : "Save Banner"}
            </button>

            <button
              type="button"
              onClick={() => {
                setBannerFile(null);
                setDoorTime("");
                setIsEditMode(false);
                setError("");
                setSuccess("");
              }}
              style={{
                padding: "8px 16px",
                borderRadius: 8,
                border: "1px solid #d1d5db",
                backgroundColor: "white",
                color: "#374151",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Reset
            </button>
          </div>
        </form>
      </div>

      <div
        style={{
          marginTop: 24,
          padding: 16,
          borderRadius: 12,
          backgroundColor: "#ffffff",
          border: "1px solid #e5e7eb",
          boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
        }}
      >
        <h3 style={{ marginBottom: 14, fontSize: 20, fontWeight: 700 }}>
          Conference Photos
        </h3>

        <form
          onSubmit={handleAddPhotos}
          style={{
            marginBottom: 20,
            padding: 14,
            borderRadius: 10,
            backgroundColor: "#f9fafb",
            border: "1px solid #e5e7eb",
          }}
        >
          <label
            style={{
              display: "block",
              marginBottom: 8,
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            Add Multiple Photos
          </label>

          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => setPhotoFiles(Array.from(e.target.files || []))}
          />

          {photoFiles.length > 0 && (
            <div style={{ marginTop: 10, fontSize: 13, color: "#374151" }}>
              Selected files: {photoFiles.map((file) => file.name).join(", ")}
            </div>
          )}

          <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="submit"
              disabled={!selectedConference || photoSubmitting}
              style={{
                padding: "8px 16px",
                borderRadius: 8,
                border: "none",
                backgroundColor: "#16a34a",
                color: "white",
                fontWeight: 500,
                cursor: !selectedConference || photoSubmitting ? "not-allowed" : "pointer",
              }}
            >
              {photoSubmitting ? "Uploading..." : "Upload Photos"}
            </button>

            <button
              type="button"
              onClick={() => fetchConferencePhotos(selectedConference)}
              disabled={!selectedConference || photoLoading}
              style={{
                padding: "8px 16px",
                borderRadius: 8,
                border: "none",
                backgroundColor: "#9B1321",
                color: "white",
                fontWeight: 500,
                cursor: !selectedConference || photoLoading ? "not-allowed" : "pointer",
              }}
            >
              {photoLoading ? "Loading..." : "Get Photos"}
            </button>
          </div>
        </form>

        {photoLoading ? (
          <p>Loading conference photos...</p>
        ) : conferencePhotos.length > 0 ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 16,
            }}
          >
            {conferencePhotos.map((item) => (
              <div
                key={item.id}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: 12,
                  backgroundColor: "#fff",
                }}
              >
                <img
                  src={item.photo_url}
                  alt={`Conference Photo ${item.id}`}
                  style={{
                    width: "100%",
                    height: 180,
                    objectFit: "cover",
                    borderRadius: 10,
                    border: "1px solid #e5e7eb",
                    marginBottom: 10,
                  }}
                />

                <p style={{ fontSize: 13, color: "#374151", marginBottom: 6 }}>
                  Photo ID: {item.id}
                </p>

                <p style={{ fontSize: 13, color: "#374151", marginBottom: 10 }}>
                  Conference ID: {item.conference_id}
                </p>

                {editingPhotoId === item.id ? (
                  <div style={{ marginBottom: 10 }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        setEditPhotoFile(
                          e.target.files && e.target.files[0] ? e.target.files[0] : null
                        )
                      }
                    />

                    {editPhotoFile && (
                      <p style={{ marginTop: 8, fontSize: 12, color: "#374151" }}>
                        Selected: {editPhotoFile.name}
                      </p>
                    )}

                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        marginTop: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => handleEditPhoto(item.id)}
                        disabled={photoSubmitting}
                        style={{
                          padding: "7px 12px",
                          borderRadius: 8,
                          border: "none",
                          backgroundColor: "#2563eb",
                          color: "#fff",
                          cursor: photoSubmitting ? "not-allowed" : "pointer",
                        }}
                      >
                        Update
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setEditingPhotoId(null);
                          setEditPhotoFile(null);
                        }}
                        style={{
                          padding: "7px 12px",
                          borderRadius: 8,
                          border: "1px solid #d1d5db",
                          backgroundColor: "#fff",
                          color: "#374151",
                          cursor: "pointer",
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingPhotoId(item.id);
                        setEditPhotoFile(null);
                        setError("");
                        setSuccess("");
                      }}
                      style={{
                        padding: "7px 12px",
                        borderRadius: 8,
                        border: "none",
                        backgroundColor: "#2563eb",
                        color: "#fff",
                        cursor: "pointer",
                      }}
                    >
                      Edit
                    </button>

                    <button
                      type="button"
                      onClick={() => handleDeletePhoto(item.id)}
                      disabled={photoSubmitting}
                      style={{
                        padding: "7px 12px",
                        borderRadius: 8,
                        border: "none",
                        backgroundColor: "#dc2626",
                        color: "#fff",
                        cursor: photoSubmitting ? "not-allowed" : "pointer",
                      }}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p>
            {selectedConference
              ? "No photos found for this conference."
              : "Please select a conference first."}
          </p>
        )}
      </div>

      <div
        style={{
          marginTop: 24,
          padding: 16,
          borderRadius: 12,
          backgroundColor: "#ffffff",
          border: "1px solid #e5e7eb",
          boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
        }}
      >
        <h3 style={{ marginBottom: 14, fontSize: 20, fontWeight: 700 }}>
          Conference Video
        </h3>

        {videoLoading ? (
          <p>Loading conference video links...</p>
        ) : (
          <form
            onSubmit={handleSaveConferenceVideos}
            style={{
              padding: 14,
              borderRadius: 10,
              backgroundColor: "#f9fafb",
              border: "1px solid #e5e7eb",
            }}
          >
            <label
              style={{
                display: "block",
                marginBottom: 8,
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              Add Video Links
            </label>

            {videoLinks.map((link, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  marginBottom: 10,
                  flexWrap: "wrap",
                }}
              >
                <input
                  type="text"
                  value={link}
                  placeholder={`Conference video link ${index + 1}`}
                  onChange={(e) => handleVideoChange(index, e.target.value)}
                  style={{
                    flex: 1,
                    minWidth: "260px",
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                    fontSize: 14,
                    backgroundColor: "#fff",
                  }}
                />

                <button
                  type="button"
                  onClick={handleAddVideoField}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: "none",
                    backgroundColor: "#16a34a",
                    color: "white",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  +
                </button>

                <button
                  type="button"
                  onClick={() => handleRemoveVideoField(index)}
                  style={{
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: "none",
                    backgroundColor: "#dc2626",
                    color: "white",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Delete
                </button>
              </div>
            ))}

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
              <button
                type="submit"
                disabled={!selectedConference || videoSubmitting}
                style={{
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: "none",
                  backgroundColor: "#2563eb",
                  color: "white",
                  fontWeight: 500,
                  cursor: !selectedConference || videoSubmitting ? "not-allowed" : "pointer",
                }}
              >
                {videoSubmitting ? "Saving..." : "Save Videos"}
              </button>

              <button
                type="button"
                onClick={() => fetchConferenceVideos(selectedConference)}
                disabled={!selectedConference || videoLoading}
                style={{
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: "none",
                  backgroundColor: "#9B1321",
                  color: "white",
                  fontWeight: 500,
                  cursor: !selectedConference || videoLoading ? "not-allowed" : "pointer",
                }}
              >
                {videoLoading ? "Loading..." : "Get Videos"}
              </button>
            </div>

            {/* {videoLinks.some((item) => item.trim()) && (
              <div style={{ marginTop: 16 }}>
                <h4 style={{ fontSize: 15, marginBottom: 10, color: "#111827" }}>
                  Preview Links
                </h4>
                <div style={{ display: "grid", gap: 8 }}>
                  {videoLinks
                    .filter((item) => item.trim())
                    .map((item, idx) => (
                      <a
                        key={idx}
                        href={item}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#2563eb",
                          textDecoration: "underline",
                          wordBreak: "break-all",
                          fontSize: 14,
                        }}
                      >
                        {item}
                      </a>
                    ))}
                </div>
              </div>
            )} */}
          </form>
        )}
      </div>
    </div>
  );
}